import React, { useRef, useState } from "react";
import {
    Platform,
    StyleSheet,
    useWindowDimensions,
    View,
    Image,
    Text,
    TouchableOpacity,
} from "react-native";
import { Tree, TreeNode } from 'react-organizational-chart';
import { useTranslation } from "react-i18next";
import { useTheme } from "@react-navigation/native";

const OrgChartScreen = (props) => {
    const [scale, setScale] = useState(1);
    const { width, height } = useWindowDimensions();
    const { t } = useTranslation();
    const { colors } = useTheme();
    const styles = makeStyles(colors, height, width);
    const containerRef = useRef(null);
    const isDragging = useRef(false);
    const dragStart = useRef({ x: 0, y: 0 });
    const scrollStart = useRef({ left: 0, top: 0 });

    const zoomIn = () => setScale(s => Math.min(s + 0.1, 2));
    const zoomOut = () => setScale(s => Math.max(s - 0.1, 0.3));
    const resetZoom = () => setScale(1);

    const onMouseDown = e => {
        isDragging.current = true;
        dragStart.current = { x: e.clientX, y: e.clientY };
        if (containerRef.current) {
            scrollStart.current = {
                left: containerRef.current.scrollLeft,
                top: containerRef.current.scrollTop,
            };
        }
    };
    const onMouseMove = e => {
        if (!isDragging.current || !containerRef.current) return;
        containerRef.current.scrollLeft = scrollStart.current.left - (e.clientX - dragStart.current.x);
        containerRef.current.scrollTop = scrollStart.current.top - (e.clientY - dragStart.current.y);
    };
    const onMouseUp = () => { isDragging.current = false; };
    const onMouseLeave = () => { isDragging.current = false; };

    // ── unchanged logic helpers ──
    const isEmployeeAdded = (manId, empl) => {
        if (empl.sEmplId === manId) return empl;
        let found;
        empl.Employees?.forEach(inner => {
            if (!found) found = isEmployeeAdded(manId, inner);
        });
        return found;
    };

    // ── Get initials from name ──
    const getInitials = name =>
        (name || '').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

    // ── Avatar component ──
    const NodeAvatar = ({ photo, name, size = 44 }) => {
        const initials = getInitials(name);
        if (photo) {
            return (
                <Image
                    style={{
                        width: size,
                        height: size,
                        borderRadius: size / 2,
                        borderWidth: 2,
                        borderColor: colors.border_card,
                    }}
                    source={{ uri: `data:image/png;base64,${photo}` }}
                />
            );
        }
        return (
            <View style={{
                width: size,
                height: size,
                borderRadius: size / 2,
                backgroundColor: colors.green_dark,
                borderWidth: 2,
                borderColor: colors.green_pale,
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                <Text style={{
                    fontSize: size * 0.33,
                    fontWeight: '800',
                    color: colors.background_white,
                    fontFamily: 'Plus Jakarta Sans',
                }}>
                    {initials}
                </Text>
            </View>
        );
    };

    // ── Employee row (photo + name/role) ──
    const DrawEmployeeCard = ({ item, isHead = false }) => {
        if (!item) return null;
        return (
            <View style={[styles.employeeRow, isHead && styles.employeeRowHead]}>
                <NodeAvatar
                    photo={item.imPhoto}
                    name={item.sName}
                    size={isHead ? 38 : 32}
                />
                <View style={styles.employeeInfo}>
                    <Text style={[styles.employeeName, isHead && styles.employeeNameHead]}
                        numberOfLines={1}>
                        {item.sName}
                    </Text>
                    {item.sSeniorityVisiblity && item.sSeniority ? (
                        <Text style={styles.employeeRole} numberOfLines={1}>
                            {item.sSeniority}
                        </Text>
                    ) : null}
                    {item.sJobTitleVisibility && item.sJobTitle ? (
                        <Text style={styles.employeeJobTitle} numberOfLines={1}>
                            {item.sJobTitle}
                        </Text>
                    ) : null}
                </View>
            </View>
        );
    };

    // ── Dept pill ──
    const DeptPill = ({ label }) => (
        <View style={styles.deptPill}>
            <Text style={styles.deptPillText}>{label}</Text>
        </View>
    );

    // ── Org chart box (the node card) ──
    const DrawOrgChartBox = ({ item, isRoot = false }) => {
        if (!item) return null;
        const hasHead = item.cEmpHead !== null && item.cEmpHead !== undefined;
        const hasCEO = item.CEO !== undefined && item.CEO !== null;
        const employees = item.Employees || [];

        return (
            <View style={[styles.nodeCard, isRoot && styles.nodeCardRoot]}>

                {/* Node / dept title */}
                <View style={styles.nodeTitleRow}>
                    <View style={[
                        styles.nodeTitleIcon,
                        isRoot && { backgroundColor: colors.green_dark },
                    ]}>
                        {/* small colored dot */}
                        <View style={{
                            width: 8, height: 8, borderRadius: 4,
                            backgroundColor: isRoot ? colors.green : colors.green_dark,
                        }} />
                    </View>
                    <Text style={[styles.nodeTitle, isRoot && styles.nodeTitleRoot]}
                        numberOfLines={1}>
                        {item.sName}
                    </Text>
                </View>

                {/* Divider */}
                <View style={styles.nodeDivider} />

                {/* CEO */}
                {hasCEO && <DrawEmployeeCard item={item.CEO} isHead={true} />}

                {/* Head of node */}
                {hasHead && !hasCEO && <DrawEmployeeCard item={item.cEmpHead} isHead={true} />}

                {/* Employees */}
                {employees.length > 0 && (
                    <>
                        {(hasHead || hasCEO) && employees.length > 0 && (
                            <View style={styles.employeeSep}>
                                <View style={styles.employeeSepLine} />
                                <Text style={styles.employeeSepText}>
                                    {employees.length} member{employees.length !== 1 ? 's' : ''}
                                </Text>
                                <View style={styles.employeeSepLine} />
                            </View>
                        )}
                        {employees.map((emp, i) => (
                            <DrawEmployeeCard key={emp.sEmplId || i} item={emp} />
                        ))}
                    </>
                )}

                {/* Employee count badge if list is long */}
                {employees.length > 3 && (
                    <View style={styles.countBadge}>
                        <Text style={styles.countBadgeText}>+{employees.length - 3} more</Text>
                    </View>
                )}
            </View>
        );
    };

    // ── Tree renderer (your existing logic, unchanged) ──
    const OrgChartTree = () => {
        let company = null, func = null, servLine = null, dep = null;
        let empl = null, tempEmployee = null;
        let heads = [], horder = 1000;

        if (props.orgChart !== null) {
            const seniorityFlag = true;
            const jobTitleFlag = true;

            props.orgChart?.forEach(dr => {
                let bytes = dr.employeePhotograph || null;
                if (!bytes) bytes = dr.employeeGender === 'M' ? dr.emptyMalePhoto : dr.emptyFemalePhoto;
                const photo = bytes || null;

                func = { sCode: dr.functionID, sName: dr.function, cEmpHead: null, ServiceLines: [] };
                servLine = { sCode: dr.serviceLineID, sName: dr.serviceLine, cEmpHead: null, Departments: [] };
                dep = { sCode: dr.departmentID, sName: dr.department, cEmpHead: null, Employees: [] };

                empl = {
                    sEmplId: dr.employeeID,
                    sName: dr.employeeName,
                    imPhoto: photo,
                    sSeniority: dr.employeeSeniority,
                    sJobTitle: dr.jobTitle,
                    sSeniorityVisiblity: seniorityFlag,
                    sJobTitleVisibility: jobTitleFlag,
                    Employees: [],
                };

                if (!company) company = { sCode: dr.companyID, sName: dr.companyName, CEO: null, Functions: [] };

                if (dr.hOrder < horder) { company.CEO = empl; horder = dr.hOrder; }

                if (!heads.find(e => e.sEmplId === empl.sEmplId)) {
                    if (dr.isHead) { heads.push(empl); return; }
                }

                if (!company.Functions.find(f => f.sCode === func.sCode)) company.Functions.push(func);
                else func = company.Functions.find(f => f.sCode === func.sCode);

                if (!func.ServiceLines.find(sl => sl.sCode === servLine.sCode)) func.ServiceLines.push(servLine);
                else servLine = func.ServiceLines.find(sl => sl.sCode === servLine.sCode);

                if (!servLine.Departments.find(d => d.sCode === dep.sCode)) servLine.Departments.push(dep);
                else dep = servLine.Departments.find(d => d.sCode === dep.sCode);

                if (!func.cEmpHead && dr.functionHeadID) {
                    tempEmployee = heads.find(e => e.sEmplId === dr.functionHeadID);
                    if (tempEmployee) func.cEmpHead = tempEmployee;
                }
                if (!servLine.cEmpHead && dr.serviceLineHeadID) {
                    tempEmployee = heads.find(e => e.sEmplId === dr.serviceLineHeadID);
                    if (tempEmployee) servLine.cEmpHead = tempEmployee;
                }
                if (!dep.cEmpHead && dr.departmentHeadID) {
                    tempEmployee = heads.find(e => e.sEmplId === dr.departmentHeadID);
                    if (tempEmployee) dep.cEmpHead = tempEmployee;
                }

                if (!dep.Employees.find(e => e.sEmplId === empl.sEmplId)) {
                    if (dr.managerID !== null) {
                        tempEmployee = heads.find(e => e.sEmplId === dr.managerID);
                        if (tempEmployee !== undefined) {
                            dep.Employees.push(empl);
                        } else {
                            let man = null;
                            for (const e of dep.Employees) {
                                man = isEmployeeAdded(dr.managerID, e);
                                if (man !== undefined) { man.Employees.push(empl); break; }
                            }
                        }
                    }
                }
            });
        }

        return (
            <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

                {/* ── Toolbar ── */}
                <div style={webStyles.toolbar}>
                    <div style={webStyles.toolbarTitle}>
                        {/* git-network icon substitute */}
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#5C8F1A" strokeWidth="2" strokeLinecap="round">
                            <circle cx="6" cy="6" r="3" /><circle cx="18" cy="18" r="3" /><circle cx="18" cy="6" r="3" />
                            <line x1="6" y1="9" x2="6" y2="21" /><line x1="6" y1="21" x2="15.5" y2="15.5" />
                            <line x1="18" y1="9" x2="18" y2="15" />
                        </svg>
                        Organisation Chart
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={webStyles.zoomGroup}>
                            <button style={webStyles.zoomBtn} onClick={zoomOut}>−</button>
                            <span style={webStyles.zoomLabel}>{Math.round(scale * 100)}%</span>
                            <button style={webStyles.zoomBtn} onClick={zoomIn}>+</button>
                        </div>
                        <button style={webStyles.toolbarBtn} onClick={resetZoom}>
                            ↺ Reset
                        </button>
                    </div>
                </div>

                {/* ── Canvas ── */}
                <div
                    ref={containerRef}
                    style={{
                        ...webStyles.canvas,
                        height: height - 160,
                        cursor: isDragging.current ? 'grabbing' : 'grab',
                    }}
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={onMouseLeave}
                >
                    <div style={{
                        transform: `scale(${scale})`,
                        transformOrigin: '50px 50px',
                        width: 'fit-content',
                        padding: '40px 60px',
                    }}>
                        {company && (
                            <Tree
                                lineWidth="2px"
                                lineColor="#E4EDD6"
                                lineBorderRadius="12px"
                                label={<DrawOrgChartBox item={company} isRoot={true} />}
                            >
                                {company.Functions?.map((functionItr, fi) => (
                                    <TreeNode
                                        key={fi}
                                        label={<DrawOrgChartBox item={functionItr} />}
                                    >
                                        {functionItr.ServiceLines?.map((serviceLineItr, si) => (
                                            <TreeNode
                                                key={si}
                                                label={<DrawOrgChartBox item={serviceLineItr} />}
                                            >
                                                {serviceLineItr.Departments?.map((deptItr, di) => (
                                                    <TreeNode
                                                        key={di}
                                                        label={<DrawOrgChartBox item={deptItr} />}
                                                    />
                                                ))}
                                            </TreeNode>
                                        ))}
                                    </TreeNode>
                                ))}
                            </Tree>
                        )}
                    </div>
                </div>

            </div>
        );
    };

    return <OrgChartTree />;
};

// ── Web-only styles (inline) ──
const webStyles = {
    toolbar: {
        background: '#FFFFFF',
        borderBottom: '1px solid #E4EDD6',
        padding: '10px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 12px rgba(92,143,26,0.08)',
    },
    toolbarTitle: {
        fontSize: 15,
        fontWeight: 800,
        color: '#1A2410',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    zoomGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        background: '#F4F7EE',
        border: '1.5px solid #E4EDD6',
        borderRadius: 9,
        padding: 3,
    },
    zoomBtn: {
        width: 28,
        height: 28,
        border: 'none',
        background: 'transparent',
        borderRadius: 6,
        cursor: 'pointer',
        fontSize: 16,
        fontWeight: 700,
        color: '#637A45',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    zoomLabel: {
        fontSize: 12,
        fontWeight: 700,
        color: '#637A45',
        minWidth: 38,
        textAlign: 'center',
    },
    toolbarBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '7px 14px',
        borderRadius: 9,
        border: '1.5px solid #E4EDD6',
        background: '#FFFFFF',
        fontSize: 12,
        fontWeight: 700,
        color: '#637A45',
        cursor: 'pointer',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
    },
    canvas: {
        background: '#F4F7EE',
        overflow: 'auto',
        userSelect: 'none',
    },
};

// ── RN StyleSheet ──
const makeStyles = (colors, _height, _width) => StyleSheet.create({

    // ── Node card ──
    nodeCard: {
        backgroundColor: colors.background_white,
        borderRadius: 12,
        borderWidth: 1.5,
        borderColor: colors.border_card,
        padding: 14,
        margin: 8,
        minWidth: Platform.OS === 'web' ? 190 : 150,
        maxWidth: Platform.OS === 'web' ? 220 : 170,
        shadowColor: 'rgba(92,143,26,1)',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 10,
        elevation: 3,
    },
    nodeCardRoot: {
        backgroundColor: colors.text_primary,
        borderColor: colors.text_approved,
        shadowOpacity: 0.18,
        shadowRadius: 16,
        elevation: 6,
    },

    // ── Node title row ──
    nodeTitleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 10,
    },
    nodeTitleIcon: {
        width: 22,
        height: 22,
        borderRadius: 6,
        backgroundColor: colors.buttons_primary,
        alignItems: 'center',
        justifyContent: 'center',
    },
    nodeTitle: {
        fontSize: 12,
        fontWeight: '800',
        color: colors.text_primary,
        flex: 1,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    nodeTitleRoot: {
        color: colors.background_white,
    },

    // ── Divider ──
    nodeDivider: {
        height: 1,
        backgroundColor: colors.border_card,
        marginBottom: 10,
    },

    // ── Employee row ──
    employeeRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingVertical: 6,
        paddingHorizontal: 4,
        borderRadius: 8,
        marginBottom: 4,
    },
    employeeRowHead: {
        backgroundColor: colors.buttons_primary,
        borderWidth: 1,
        borderColor: colors.border_card,
        paddingHorizontal: 8,
        paddingVertical: 8,
        borderRadius: 9,
        marginBottom: 8,
    },
    employeeInfo: {
        flex: 1,
        gap: 2,
    },
    employeeName: {
        fontSize: 12,
        fontWeight: '700',
        color: colors.text_primary,
    },
    employeeNameHead: {
        fontSize: 13,
        fontWeight: '800',
        color: colors.text_primary,
    },
    employeeRole: {
        fontSize: 11,
        fontWeight: '500',
        color: colors.text_secondary,
    },
    employeeJobTitle: {
        fontSize: 10,
        fontWeight: '500',
        color: colors.text_muted,
    },

    // ── Separator between head + members ──
    employeeSep: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        marginVertical: 8,
    },
    employeeSepLine: {
        flex: 1,
        height: 1,
        backgroundColor: colors.border_card,
    },
    employeeSepText: {
        fontSize: 10,
        fontWeight: '700',
        color: colors.text_muted,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },

    // ── Dept pill ──
    deptPill: {
        alignSelf: 'flex-start',
        backgroundColor: colors.buttons_primary,
        borderWidth: 1,
        borderColor: colors.border_card,
        borderRadius: 100,
        paddingVertical: 3,
        paddingHorizontal: 10,
        marginTop: 4,
    },
    deptPillText: {
        fontSize: 10,
        fontWeight: '700',
        color: colors.text_approved,
    },

    // ── Count badge ──
    countBadge: {
        alignSelf: 'center',
        backgroundColor: colors.buttons_primary,
        borderWidth: 1,
        borderColor: colors.border_card,
        borderRadius: 100,
        paddingVertical: 3,
        paddingHorizontal: 12,
        marginTop: 6,
    },
    countBadgeText: {
        fontSize: 10,
        fontWeight: '700',
        color: colors.text_approved,
    },
});

export default OrgChartScreen;